import apiClient from '../../shared/services/apiClient';

/**
 * Domain Registration Service (User Version)
 *
 * Handles domain registration, availability checks, and domain management.
 * Users can check domain availability, register domains, and manage their own domains.
 *
 * All routes use /api/user/domain-registration/* prefix
 */
export const domainRegistrationService = {
    // ==========================================
    // Domain Availability
    // ==========================================

    /**
     * Check domain availability
     * @param {string} domain - Domain name
     * @returns {Promise} Availability information
     */
    checkAvailability: async (payload) => {
        try {
            let params = {};
            if (typeof payload === 'string') {
                params.domain = payload;
            } else if (payload && typeof payload === 'object') {
                params = payload;
            }

            const response = await apiClient.get('/api/user/domain-registration/check-availability', {
                params
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Bulk domain availability check (legacy endpoint)
     * @param {Object} data - { domains: [], tlds: [], years }
     */
    bulkCheckAvailability: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain/check-availability/bulk', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Get TLD pricing
     * @param {string} tld - Top-level domain (e.g., com, net)
     * @param {number} years - Number of years
     * @returns {Promise} Pricing information
     */
    // opts.domain_id: renewal quote for one of my domains (priced on that domain's registrar)
    getTldPricing: async (tld, years = 1, opts = {}) => {
        try {
            const response = await apiClient.get('/api/user/domain-registration/tld-pricing', {
                params: { tld, years, ...opts }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // Domain Registration
    // ==========================================

    /**
     * Register domain
     * @param {Object} data - Registration information
     * @returns {Promise} Registration result
     */
    registerDomain: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/register', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Transfer domain
     * @param {Object} data - Transfer information
     * @returns {Promise} Transfer result
     */
    transferDomain: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/transfer', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // My Domains Management
    // ==========================================

    /**
     * Get my domains list
     * @param {Object} params - Query parameters
     * @returns {Promise} Domains list
     */
    getMyDomains: async (params = {}) => {
        try {
            const response = await apiClient.get('/api/user/domain-registration', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Get single domain detail
     * @param {number} id - Domain ID
     * @returns {Promise} Domain details
     */
    getDomainDetail: async (id) => {
        try {
            const response = await apiClient.get('/api/user/domain-registration/get', {
                params: { id }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Renew domain
     * @param {Object} data - Renewal information {id, years}
     * @returns {Promise} Renewal result
     */
    renewDomain: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/renew', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Update domain nameservers
     * @param {Object} data - {id, nameservers: []}
     * @returns {Promise} Update result
     */
    updateNameservers: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/update-nameservers', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Toggle domain lock status
     * @param {Object} data - {id, lock: boolean}
     * @returns {Promise} Toggle result
     */
    toggleDomainLock: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/toggle-lock', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Toggle WHOIS privacy protection
     * @param {Object} data - {id, privacy: boolean}
     * @returns {Promise} Toggle result
     */
    toggleWhoisPrivacy: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/toggle-privacy', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // 隐私模式（代理注册）：{ id, enable }；开启返回 invoice_id（按剩余期限补附加费）
    setPrivacyMode: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/privacy-mode', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    toggleAutoRenew: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/toggle-auto-renew', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Fetch TLD directory for listings
     * @param {Object} params - { view_type, category, letter, limit }
     *   view_type: 'general' (默认) 或 'idn'
     *     - general: 返回行业分类 (Business, Technology...)
     *     - idn: 返回语言分类 (中文, 俄文, 阿拉伯文...)
     */
    /** 一键使用 Yeap DNS（复用本人 zone 或开通免费 DNS 并修改注册商 NS） */
    useYeapDns: async (domainId) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/use-yeap-dns', { domain_id: domainId });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /** 转入预检：锁定 / 60 天 / 赎回 / 转移码 / 后缀转入方式 / DNS 提示（付款前） */
    precheckTransfer: async (domain, authCode) => {
        try {
            const body = { domain };
            if (authCode) body.auth_code = authCode;
            const response = await apiClient.post('/api/user/domain-registration/transfer/precheck', body);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /** 转入进度（已提交 → 等待原注册商确认 → 完成/失败） */
    getTransferStatus: async (domainId) => {
        try {
            const response = await apiClient.get('/api/user/domain-registration/transfer/status', { params: { domain_id: domainId } });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /** 取消转入（注册商支持时；完成前取消退回余额） */
    cancelTransfer: async (domainId) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/transfer/cancel', { domain_id: domainId });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /** 从注册商只读核对到期日/状态（每域名 5 分钟一次） */
    triggerSync: async (domainId) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/trigger-sync', { domain_id: domainId });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    getTldDirectory: async (params = {}) => {
        try {
            // format=lite：每个 TLD 只传一次（~1.6MB → ~120KB），这里还原成原有 {featured,categories,alphabetical,options} 形状
            const response = await apiClient.get('/api/user/domain/tld-directory', { params: { format: 'lite', ...params } });
            const body = response.data;
            const d = body?.data;
            if (d && d.tlds && !Array.isArray(d.tlds)) {
                // 行格式 [unicode|null, reg, renew, currency|null, flags, languages|null, transfer]
                // flags: bit0 = 后缀本身是 IDN（.中国），bit1 = 热门，bit2 = 接受 IDN 二级标签，bit3 = 暂未开放（无价格）
                const cache = {};
                const item = (tld) => {
                    if (cache[tld]) return cache[tld];
                    const r = d.tlds[tld] || [];
                    const uni = r[0] || tld;
                    const flags = r[4] || 0;
                    cache[tld] = {
                        tld,
                        tld_unicode: uni,
                        display_name: `.${uni}`,
                        registration_price: r[1] ?? null,
                        renewal_price: r[2] ?? null,
                        transfer_price: r[6] ?? null,
                        currency: r[3] || d.default_currency || null,
                        is_idn: !!(flags & 1),
                        is_featured: !!(flags & 2),
                        accepts_idn: !!(flags & 4),
                        available: !(flags & 8),
                        // r[7] = [特价首年价, 结束时间] | null
                        promo: Array.isArray(r[7]) ? { price: r[7][0], ends_at: r[7][1] } : null,
                        languages: Array.isArray(r[5]) ? r[5] : [],
                    };
                    return cache[tld];
                };
                const languages = {};
                Object.entries(d.languages && typeof d.languages === 'object' ? d.languages : {}).forEach(([k, l]) => {
                    languages[k] = {
                        label: l.label,
                        label_en: l.label_en,
                        count: l.count || 0,
                        items: Array.isArray(l.items) ? l.items : Object.values(l.items || {}),
                        native: Array.isArray(l.native) ? l.native : Object.values(l.native || {}),
                    };
                });
                const categories = {};
                Object.entries(d.categories || {}).forEach(([k, c]) => {
                    categories[k] = { ...c, items: (c.items || []).map(item) };
                });
                const alphabetical = {};
                Object.entries(d.alphabetical || {}).forEach(([k, list]) => {
                    alphabetical[k] = (list || []).map(item);
                });
                return {
                    ...body,
                    data: {
                        featured: (d.featured || []).map(item),
                        categories,
                        alphabetical,
                        options: Object.keys(d.tlds).map((t) => ({ label: `.${(d.tlds[t] && d.tlds[t][0]) || t}`, value: t })),
                        tlds: Object.keys(d.tlds).map(item),
                        languages,
                    },
                };
            }
            return body;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Get domain auth/transfer code
     * @param {number} id - Domain ID
     * @returns {Promise} Auth code
     */
    getAuthCode: async (id) => {
        try {
            const response = await apiClient.get('/api/user/domain/get-auth-code', {
                params: { id }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Update domain contact information
     * @param {Object} data - {id, contacts: {registrant, admin, tech, billing}}
     * @returns {Promise} Update result
     */
    updateContacts: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain/update-contacts', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },
    // ==========================================
    // DNSSEC Management (Registrar)
    // ==========================================

    /**
     * Get DNSSEC records from registrar
     * @param {number} domainId - Domain ID
     * @returns {Promise} DNSSEC records from registrar
     */
    getRegistrarDnssec: async (domainId) => {
        try {
            const response = await apiClient.get('/api/user/domain-registration/dnssec', {
                params: { domain_id: domainId }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Submit DNSSEC from DNS service to registrar
     * @param {Object} data - { domain_id, zone_id? }
     * @returns {Promise} Submit result
     */
    submitDnssecToRegistrar: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/dnssec/submit', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Delete DNSSEC from registrar
     * @param {Object} data - { domain_id, ds_record? }
     * @returns {Promise} Delete result
     */
    deleteRegistrarDnssec: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/dnssec/delete', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Get glue records for a domain
     * @param {number} domainId - Domain ID
     * @returns {Promise} Glue records list
     */
    getGlueRecords: async (domainId) => {
        try {
            const response = await apiClient.get('/api/user/domain-registration/glue-records', {
                params: { domain_id: domainId }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Add glue record
     * @param {Object} data - { domain_id, hostname, ipv4, ipv6? }
     * @returns {Promise} Add result
     */
    addGlueRecord: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/glue-records/add', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Update glue record
     * @param {Object} data - { domain_id, hostname, ipv4, ipv6? }
     * @returns {Promise} Update result
     */
    updateGlueRecord: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/glue-records/update', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Delete glue record
     * @param {Object} data - { domain_id, hostname }
     * @returns {Promise} Delete result
     */
    deleteGlueRecord: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/glue-records/delete', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 查询域名宽限/赎回期状态
     * @param {number} domainId
     * @returns {Promise} { success, data: { in_grace, in_redemption, can_restore, ... } }
     */
    getGraceRedemptionStatus: async (domainId) => {
        try {
            const response = await apiClient.get('/api/user/domain-registration/grace-redemption-status', {
                params: { domain_id: domainId }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 恢复处于赎回期的域名
     * @param {number} domainId
     * @returns {Promise} 恢复结果
     */
    restoreDomain: async (domainId) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/restore', { domain_id: domainId });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取即将到期的域名（服务端计算）
     * @param {number} [days=30]
     * @returns {Promise} { success, data: { domains, count, days } }
     */
    getExpiringDomains: async (days = 30) => {
        try {
            const response = await apiClient.get('/api/user/domain-registration/expiring', {
                params: { days }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取子域名服务器（child nameserver）列表
     * @param {number} domainId
     * @returns {Promise} child nameserver 列表
     */
    getChildNameservers: async (domainId) => {
        try {
            const response = await apiClient.get('/api/user/domain-registration/child-nameservers', {
                params: { domain_id: domainId }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 注册子域名服务器
     * @param {Object} data - { domain_id, hostname, ipv4, ipv6? }
     * @returns {Promise} 注册结果
     */
    registerChildNameserver: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/child-nameservers/register', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 更新子域名服务器
     * @param {Object} data - { domain_id, hostname, ipv4, ipv6? }
     * @returns {Promise} 更新结果
     */
    updateChildNameserver: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/child-nameservers/update', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 删除子域名服务器
     * @param {Object} data - { domain_id, hostname }
     * @returns {Promise} 删除结果
     */
    deleteChildNameserver: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-registration/child-nameservers/delete', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },
};

export default domainRegistrationService;
